import type { ConditionalConfig } from '@viapeople/components/src/components/forms/conditionalTypes'
import { v7 as uuidv7 } from 'uuid'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import type { FormComponentName, FormInputComponent } from '../types'
import { type ComponentDefinition, componentRegistry } from './componentRegistry'

interface FormBuilderState {
	componentRegistry: ComponentDefinition[]
	registerComponent: (component: ComponentDefinition) => void
	getComponentDefinition: (name: FormComponentName, isSection?: boolean) => ComponentDefinition | undefined

	formName: string
	organizationId: string
	setFormName: (name: string) => void
	setOrganizationId: (id: string) => void

	formComponents: FormInputComponent[]
	setFormComponents: (components: FormInputComponent[]) => void
	loadForm: (form: { name: string; components: FormInputComponent[] }) => void
	canPlaceChild: (componentDef?: ComponentDefinition, containerId?: string) => boolean
	addComponent: (
		name: FormComponentName,
		containerId?: string,
		index?: number,
		isSection?: boolean,
		column?: number,
	) => void
	moveComponent: (id: string, containerId: string | undefined, index: number, column?: number) => void
	deleteComponent: (id: string) => void
	cleanOrphanedConditions: (deletedComponentIds: string | string[]) => void
	duplicateComponent: (id: string) => void
	updateComponentProps: (id: string, props: Record<string, unknown>) => void

	selectedComponentId: string | null
	selectComponent: (id: string | null) => void
	isDragging: boolean
	setIsDragging: (isDragging: boolean) => void

	// Tracks in-flight asset uploads (e.g. external-link documents, images) so
	// the Save form button can wait for them to finish before persisting and
	// avoid a race that would orphan the upload or save a stale URL.
	uploadingCount: number
	beginUpload: () => void
	endUpload: () => void

	findComponentById: (id: string, components?: FormInputComponent[]) => FormInputComponent | null
	findParentComponent: (
		id: string,
		components?: FormInputComponent[],
	) => { parent: FormInputComponent | null; index: number }
}

function resolveNewComponentAction(action: FormInputComponent['action']): FormInputComponent['action'] {
	return ['create', 'delete'].includes(action ?? '') ? action : ('edit' as const)
}

export const useFormBuilderStore = create<FormBuilderState>()(
	devtools(
		immer((set, get) => ({
			componentRegistry,
			registerComponent: (component) => {
				set((state) => {
					state.componentRegistry.push(component)
				})
			},

			getComponentDefinition: (name: FormComponentName, isSection?: boolean) => {
				if (name === 'container') {
					return get().componentRegistry.find(
						(component) => component.name === name && component.defaultProps.isSection === isSection,
					)
				}
				return get().componentRegistry.find((component) => component.name === name)
			},

			formName: '',
			organizationId: '',
			setFormName: (name: string) =>
				set((state) => {
					state.formName = name
				}),
			setOrganizationId: (id: string) =>
				set((state) => {
					state.organizationId = id
				}),

			formComponents: [],
			setFormComponents: (components: FormInputComponent[]) => {
				set((state) => {
					state.formComponents = components
				})
			},
			loadForm: (form: { name: string; components: FormInputComponent[] }) => {
				set((state) => {
					state.formName = form.name
					state.formComponents = form.components
					state.selectedComponentId = null
				})
			},
			canPlaceChild: (componentDef?: ComponentDefinition, containerId?: string): boolean => {
				if (!componentDef || !containerId || componentDef.name !== 'container') {
					return true
				}
				if (componentDef.defaultProps.isSection) {
					return false
				}
				const parent = get().findComponentById(containerId)
				if (!parent) {
					return false
				}
				const { isSection } = parent?.custom as { isSection?: boolean }
				if (!isSection) {
					return false
				}
				return true
			},
			addComponent: (name, containerId, index, isSection, column) => {
				set((state) => {
					const componentDef = state.componentRegistry.find(
						(component: ComponentDefinition) =>
							component.name === name && component.defaultProps.isSection === isSection,
					)
					// When dropping into a repeatable, add to its template container
					let effectiveContainerId = containerId

					if (containerId) {
						const parent = get().findComponentById(containerId)
						if (parent?.name === 'repeatable') {
							// Find the template container (should always exist now)
							const templateContainer = parent.children?.find(
								(child) => child.name === 'container' && !child.evaluationId,
							)

							if (templateContainer) {
								// Redirect to template container
								effectiveContainerId = templateContainer.id
							}
						}
					}

					if (!componentDef) {
						return
					}

					if (!get().canPlaceChild(componentDef, effectiveContainerId)) {
						return
					}

					let permissionTemplateId = ''

					if (effectiveContainerId) {
						const container = get().findComponentById(effectiveContainerId)
						if (container?.permissionTemplateId) {
							permissionTemplateId = container.permissionTemplateId
						}
					}

					const defaultLabel = (componentDef.defaultProps.label as string) || ''
					const newComponent: FormInputComponent = {
						id: uuidv7(),
						name: componentDef.name,
						custom: { ...componentDef.defaultProps, permissionTemplateId },
						children: [],
						type: componentDef.type,
						permissionTemplateId,
						required: false,
						containerId: effectiveContainerId || null,
						label: defaultLabel,
						reportLabel: defaultLabel,
						evaluationId: null,
						action: 'create',
					}

					// If creating a repeatable, automatically create its template container
					if (componentDef.name === 'repeatable') {
						const templateContainer: FormInputComponent = {
							id: uuidv7(),
							name: 'container',
							type: 'container',
							custom: { label: '', isSection: false, permissionTemplateId },
							children: [],
							permissionTemplateId,
							required: false,
							containerId: newComponent.id,
							label: '',
							reportLabel: '',
							evaluationId: null,
							action: 'create',
						}
						newComponent.children = [templateContainer]
					}

					// Assign column if adding to a columned section
					if (effectiveContainerId && column !== undefined) {
						;(newComponent.custom as Record<string, unknown>).column = column
					}

					if (!effectiveContainerId) {
						if (index !== undefined) {
							const before = state.formComponents.slice(0, index)
							const after = state.formComponents.slice(index)
							state.formComponents = [...before, newComponent, ...after]
						} else {
							state.formComponents = [...state.formComponents, newComponent]
						}
						state.selectedComponentId = newComponent.id
						return
					}
					const updateComponentChildren = (
						components: FormInputComponent[],
						containerId: string,
						newComponent: FormInputComponent,
						insertIndex?: number,
					): FormInputComponent[] => {
						return components.map((comp) => {
							if (comp.id === containerId) {
								let updatedChildren: FormInputComponent[]

								if (insertIndex !== undefined) {
									const before = comp.children?.slice(0, insertIndex)
									const after = comp.children?.slice(insertIndex)
									updatedChildren = [...(before || []), newComponent, ...(after || [])]
								} else {
									updatedChildren = [...(comp.children || []), newComponent]
								}

								return {
									...comp,
									action: resolveNewComponentAction(comp.action),
									children: updatedChildren,
								}
							}

							if (comp.children && comp.children.length > 0) {
								return {
									...comp,
									children: updateComponentChildren(comp.children, containerId, newComponent, insertIndex),
								}
							}

							return comp
						})
					}

					state.formComponents = updateComponentChildren(
						state.formComponents,
						effectiveContainerId,
						newComponent,
						index,
					)
					state.selectedComponentId = newComponent.id
				})
			},

			moveComponent: (id, containerId, index, column) => {
				set((state) => {
					let componentToMove: FormInputComponent | null = null

					const findComponent = (components: FormInputComponent[], targetId: string): FormInputComponent | null => {
						for (const comp of components) {
							if (comp.id === targetId) return comp
							if (comp.children && comp.children.length > 0) {
								const found = findComponent(comp.children, targetId)
								if (found) return found
							}
						}
						return null
					}

					const originalComponent = findComponent(state.formComponents, id)
					if (!originalComponent) {
						return
					}

					componentToMove = JSON.parse(
						JSON.stringify({
							...originalComponent,
							action: resolveNewComponentAction(originalComponent.action),
						}),
					)

					if (!componentToMove) {
						return
					}

					// When moving into a repeatable, redirect to its template container
					let effectiveContainerId = containerId

					if (containerId) {
						const parent = get().findComponentById(containerId)
						if (parent?.name === 'repeatable') {
							const templateContainer = parent.children?.find(
								(child) => child.name === 'container' && !child.evaluationId,
							)
							if (templateContainer) {
								effectiveContainerId = templateContainer.id
							}
						}
					}

					const { isSection } = componentToMove.custom as { isSection?: boolean }
					if (
						!get().canPlaceChild(get().getComponentDefinition(componentToMove.name, isSection), effectiveContainerId)
					) {
						return
					}

					const removeComponent = (components: FormInputComponent[], targetId: string): FormInputComponent[] => {
						return components.filter((comp) => {
							if (comp.id === targetId) {
								return false
							}

							if (comp.children && comp.children.length > 0) {
								comp.children = removeComponent(comp.children, targetId)
							}

							return true
						})
					}

					state.formComponents = removeComponent(state.formComponents, id)

					// Update column assignment
					if (column !== undefined) {
						;(componentToMove.custom as Record<string, unknown>).column = column
					} else {
						delete (componentToMove.custom as Record<string, unknown>).column
					}

					if (!effectiveContainerId) {
						const before = state.formComponents.slice(0, index).map((c) => ({ ...c, action: 'edit' as const }))
						const after = state.formComponents.slice(index).map((c) => ({ ...c, action: 'edit' as const }))
						state.formComponents = [...before, { ...componentToMove, containerId: null }, ...after]
					} else {
						const updateComponentChildren = (
							components: FormInputComponent[],
							targetContainerId: string,
							componentToInsert: FormInputComponent,
							insertIndex: number,
						): FormInputComponent[] => {
							return components.map((comp) => {
								if (comp.id === targetContainerId) {
									const before = comp.children?.slice(0, insertIndex)
									const after = comp.children?.slice(insertIndex)

									return {
										...comp,
										action: resolveNewComponentAction(comp.action),
										children: [
											...(before || []),
											{
												...componentToInsert,
												containerId: targetContainerId,
												custom: {
													...(componentToInsert.custom as Record<string, unknown>),
													permissionTemplateId: comp.permissionTemplateId,
												},
											},
											...(after || []),
										],
									}
								}

								if (comp.children && comp.children.length > 0) {
									return {
										...comp,
										children: updateComponentChildren(comp.children, targetContainerId, componentToInsert, insertIndex),
									}
								}

								return comp
							})
						}

						state.formComponents = updateComponentChildren(
							state.formComponents,
							effectiveContainerId,
							componentToMove,
							index,
						)
					}
				})
			},

			deleteComponent: (id) => {
				set((state) => {
					// Helper to mark component and all children as deleted
					const markAsDeleted = (component: FormInputComponent): FormInputComponent => {
						const updated = { ...component, action: 'delete' as const }
						if (updated.children && updated.children.length > 0) {
							updated.children = updated.children.map(markAsDeleted)
						}
						return updated
					}

					const removeComponent = (components: FormInputComponent[], targetId: string): FormInputComponent[] => {
						return components.map((comp) => {
							if (targetId) {
								if (comp.id === targetId) {
									// Mark this component and all its children as deleted
									return markAsDeleted(comp)
								}
								if (comp.children && comp.children.length > 0) {
									comp.children = removeComponent(comp.children, targetId)
								}
							}

							return comp
						})
					}

					state.formComponents = removeComponent(state.formComponents, id)

					if (state.selectedComponentId === id) {
						state.selectedComponentId = null
					}
				})
			},

			cleanOrphanedConditions: (deletedComponentIds) => {
				const idsToRemove =
					typeof deletedComponentIds === 'string' ? new Set([deletedComponentIds]) : new Set(deletedComponentIds)
				set((state) => {
					const walk = (components: FormInputComponent[]) => {
						for (const comp of components) {
							const custom = comp.custom as Record<string, unknown> | null
							const conditional = custom?.conditional as ConditionalConfig | undefined

							if (conditional && conditional.conditions.length > 0) {
								const original = conditional.conditions.length
								conditional.conditions = conditional.conditions.filter((c) => !idsToRemove.has(c.sourceComponentId))

								if (conditional.conditions.length !== original) {
									if (conditional.conditions.length === 0) {
										delete (comp.custom as Record<string, unknown>).conditional
									}
									comp.action = resolveNewComponentAction(comp.action)
								}
							}

							if (comp.children && comp.children.length > 0) {
								walk(comp.children as FormInputComponent[])
							}
						}
					}
					walk(state.formComponents)
				})
			},

			duplicateComponent: (id) => {
				set((state) => {
					// Map to track old component IDs to new component IDs
					const idMapping = new Map<string, string>()

					const deepClone = (comp: FormInputComponent, parentId?: string): FormInputComponent => {
						const newId = uuidv7()
						idMapping.set(comp.id, newId)

						return {
							...comp,
							action: 'create',
							id: newId,
							...(parentId ? { containerId: parentId } : {}),
							children: comp.children?.map((child) => deepClone(child, newId)) || [],
						}
					}

					// Update selectedComponentIds in average components after cloning
					const updateAverageComponentIds = (comp: FormInputComponent): FormInputComponent => {
						// Always process children first
						const processedChildren = comp.children?.map(updateAverageComponentIds)

						// Update average component's selectedComponentIds if applicable
						if (comp.name === 'average' && comp.custom) {
							const custom = comp.custom as Record<string, unknown>
							if (Array.isArray(custom.selectedComponentIds)) {
								// Remap IDs that exist in the duplicated tree, keep others unchanged
								const updatedSelectedIds = custom.selectedComponentIds.map(
									(oldId: string) => idMapping.get(oldId) || oldId,
								)

								return {
									...comp,
									custom: {
										...custom,
										selectedComponentIds: updatedSelectedIds,
									},
									children: processedChildren,
								}
							}
						}

						// Return component with processed children if any
						if (processedChildren) {
							return {
								...comp,
								children: processedChildren,
							}
						}

						return comp
					}

					const findComponent = (components: FormInputComponent[], targetId: string): FormInputComponent | null => {
						for (const comp of components) {
							if (comp.id === targetId) return comp
							if (comp.children && comp.children.length > 0) {
								const found = findComponent(comp.children, targetId)
								if (found) return found
							}
						}
						return null
					}

					const originalComponent = findComponent(state.formComponents, id)
					if (!originalComponent) {
						return
					}

					const duplicate = deepClone(originalComponent)
					const updatedDuplicate = updateAverageComponentIds(duplicate)

					const findParentAndInsertDuplicate = (
						components: FormInputComponent[],
						targetId: string,
						duplicate: FormInputComponent,
					): FormInputComponent[] => {
						return components.map((comp) => {
							if (comp.id === targetId) {
								return comp
							}

							for (let i = 0; i < (comp.children?.length || 0); i++) {
								if (comp.children?.[i].id === targetId) {
									const updatedChildren = [
										...(comp.children?.slice(0, i + 1) || []),
										duplicate,
										...(comp.children?.slice(i + 1) || []),
									]

									return {
										...comp,
										children: updatedChildren,
									}
								}
							}

							if (comp.children && comp.children.length > 0) {
								return {
									...comp,
									children: findParentAndInsertDuplicate(comp.children, targetId, duplicate),
								}
							}

							return comp
						})
					}

					const rootIndex = state.formComponents.findIndex((comp) => comp.id === id)
					if (rootIndex !== -1) {
						const before = state.formComponents.slice(0, rootIndex + 1)
						const after = state.formComponents.slice(rootIndex + 1)
						state.formComponents = [...before, updatedDuplicate, ...after]
					} else {
						state.formComponents = findParentAndInsertDuplicate(state.formComponents, id, updatedDuplicate)
					}
				})
			},

			updateComponentProps: (id, props) => {
				set((state) => {
					const updateComponentInTree = (
						components: FormInputComponent[],
						inheritedPermissionId?: string,
					): FormInputComponent[] => {
						return components.map((comp) => {
							if (comp.id === id) {
								const updatedComponent = {
									...comp,
									action: resolveNewComponentAction(comp.action),
									// Only update permissionTemplateId at the top level if explicitly provided
									...(props.permissionTemplateId !== undefined && {
										permissionTemplateId: props.permissionTemplateId as string,
									}),
									// All other properties (including label) go into custom
									custom: { ...(comp.custom as Record<string, unknown>), ...props },
								}

								if (props.label !== undefined && props.reportLabel === undefined) {
									updatedComponent.label = props.label as string

									//here the new sync:
									const currentReportLabel = comp.reportLabel
									const currentLabel = comp.label
									if (!currentReportLabel || currentReportLabel === currentLabel) {
										updatedComponent.reportLabel = props.label as string
									}
								}

								// If only reportLabel is being updated, just update that
								if (props.reportLabel !== undefined) {
									const reportLabel = props.reportLabel as string
									updatedComponent.reportLabel = reportLabel ? reportLabel : updatedComponent.label
								}

								if (
									['container', 'repeatable'].includes(comp.name) &&
									props.permissionTemplateId &&
									props.permissionTemplateId !== comp.permissionTemplateId &&
									updatedComponent.children?.length
								) {
									updatedComponent.children = updateComponentInTree(
										updatedComponent.children,
										props.permissionTemplateId as string,
									)
								}

								return updatedComponent
							}

							const shouldUpdatePermission = inheritedPermissionId !== undefined
							const updatedComp = shouldUpdatePermission
								? {
										...comp,
										action: resolveNewComponentAction(comp.action),
										permissionTemplateId: inheritedPermissionId,
										custom: {
											...(comp.custom as Record<string, unknown>),
											permissionTemplateId: inheritedPermissionId,
										},
									}
								: {
										...comp,
										action: resolveNewComponentAction(comp.action),
									}

							if (comp.children && comp.children.length > 0) {
								return {
									...updatedComp,
									children: updateComponentInTree(comp.children, inheritedPermissionId),
								}
							}
							return updatedComp
						})
					}
					state.formComponents = updateComponentInTree(state.formComponents)

					// Handle column reduction - reassign children in removed columns
					if (props.columns !== undefined) {
						const rawColumns = Number(props.columns)
						const newColumns = Math.max(1, Math.min(4, Number.isFinite(rawColumns) ? rawColumns : 1))
						const reassignChildren = (comps: FormInputComponent[]): boolean => {
							for (const comp of comps) {
								if (comp.id === id && comp.children?.length) {
									// For repeatables, field children are inside the template container (one level deeper)
									// For sections/containers, field children are direct children
									const childrenToReassign: FormInputComponent[] =
										comp.name === 'repeatable'
											? comp.children
													.filter((c) => c.name === 'container' && !c.evaluationId)
													.flatMap((c) => c.children || [])
											: comp.children

									for (const child of childrenToReassign) {
										const childCustom =
											typeof child.custom === 'object' && child.custom !== null
												? (child.custom as Record<string, unknown>)
												: {}
										const rawCol = Number(childCustom.column ?? 0)
										const childColumn = Number.isFinite(rawCol) ? rawCol : 0
										if (childColumn >= newColumns) {
											childCustom.column = newColumns - 1
											child.action = resolveNewComponentAction(child.action)
										}
									}
									return true
								}
								if (comp.children?.length && reassignChildren(comp.children)) {
									return true
								}
							}
							return false
						}
						reassignChildren(state.formComponents)
					}
				})
			},

			// UI State
			selectedComponentId: null,

			selectComponent: (id) => {
				set((state) => {
					state.selectedComponentId = id
				})
			},

			isDragging: false,

			setIsDragging: (isDragging) => {
				set((state) => {
					state.isDragging = isDragging
				})
			},

			uploadingCount: 0,

			beginUpload: () => {
				set((state) => {
					state.uploadingCount += 1
				})
			},

			endUpload: () => {
				set((state) => {
					state.uploadingCount = Math.max(0, state.uploadingCount - 1)
				})
			},

			// Utility functions
			findComponentById: (id, components) => {
				const allComponents = components || get().formComponents

				for (const component of allComponents) {
					if (component.id === id) {
						return component
					}

					if (component.children && component.children.length > 0) {
						const found = get().findComponentById(id, component.children)
						if (found) return found
					}
				}

				return null
			},

			findParentComponent: (id, components) => {
				const allComponents = components || get().formComponents

				const findById = (component: FormInputComponent): boolean => component.id === id

				const rootIndex = allComponents.findIndex(findById)
				if (rootIndex !== -1) {
					return { parent: null, index: rootIndex }
				}

				for (const component of allComponents) {
					const childIndex = component.children?.findIndex(findById)

					if (childIndex && childIndex !== -1) {
						return { parent: component, index: childIndex }
					}

					if (component.children && component.children.length > 0) {
						const result = get().findParentComponent(id, component.children)
						if (result.parent !== null || result.index !== -1) {
							return result
						}
					}
				}

				return { parent: null, index: -1 }
			},
		})),
		{ name: 'form-builder-store' },
	),
)
